/**
 * Grand Quack Auto — "QUACKED" death/end screen.
 *
 * GTA "WASTED" parody. Shows QUACKED text, then transitions to a
 * share-first screen with stats, social buttons, and PLAY AGAIN.
 * Does NOT auto-respawn — waits for player to share or replay.
 */

import { getCurrentTierName, getCurrentTierIndex } from '../systems/duckEvolutionSystem';
import { getTotalScared } from '../systems/npcSystem';
import type { World } from '../types';
import { forceShowStatsScreen } from './statsScreen';

let active = false;
const GAME_URL = 'https://grand-quack-auto.pages.dev';

export function triggerDeathScreen(world: World): void {
  if (active) return;
  active = true;

  const isMaxWanted = world.chaos.wanted >= 3;
  const deathText = isMaxWanted ? 'PLUCKED' : 'QUACKED';

  // Don't show if stats screen is already up.
  if (document.getElementById('stats-screen')) return;

  // Desaturate the canvas.
  const canvas = world.renderer.domElement;
  canvas.style.filter = 'grayscale(80%) brightness(0.6)';

  // Phase 1: Show big "QUACKED" text for 2 seconds.
  const overlay = document.createElement('div');
  overlay.id = 'death-screen';
  overlay.innerHTML = `<div class="death-text">${deathText}</div>`;
  document.body.appendChild(overlay);

  // Phase 2: After 2s, replace with share/replay screen.
  setTimeout(() => {
    const tier = getCurrentTierIndex();
    const tierName = getCurrentTierName();
    const stars = '\u2605'.repeat(Math.min(tier, 3));
    const chaos = Math.floor(world.chaos.points);
    const scared = getTotalScared();
    const time = formatTime(world.time.elapsed);

    const shareText = encodeURIComponent(
      `🦆 I got ${deathText} in Grand Quack Auto!\n` +
      `${stars} ${tierName.toUpperCase()} | ${chaos} chaos | ${scared} scared\n` +
      `Can you survive longer? #GrandQuackAuto #VibeJam2026`
    );
    const shareUrl = encodeURIComponent(GAME_URL);

    overlay.innerHTML = `
      <div class="death-card">
        <div class="death-title">${deathText}</div>
        <div class="death-tier">${stars} ${tierName.toUpperCase()} ${stars}</div>
        <div class="death-stats">
          <span>${chaos} CHAOS</span> · <span>${scared} SCARED</span> · <span>${time}</span>
        </div>
        <div class="death-share-label">SHARE YOUR DEMISE</div>
        <div class="death-share-row">
          <a class="death-btn death-tw" href="https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}" target="_blank">𝕏 Tweet</a>
          <a class="death-btn death-fb" href="https://www.facebook.com/sharer/sharer.php?u=${shareUrl}" target="_blank">f Share</a>
          <button class="death-btn death-cp" id="death-copy">📋 Copy</button>
          <button class="death-btn death-ss" id="death-screenshot">📸 Save</button>
        </div>
        <div class="death-actions">
          <button class="death-play-again" id="death-retry">🔄 PLAY AGAIN</button>
          <button class="death-play-again" id="death-stats">📊 VIEW STATS</button>
        </div>
      </div>
    `;

    // Copy share text.
    document.getElementById('death-copy')?.addEventListener('click', () => {
      navigator.clipboard?.writeText(decodeURIComponent(shareText) + '\n' + GAME_URL);
      const btn = document.getElementById('death-copy');
      if (btn) { btn.textContent = '✅'; setTimeout(() => { btn.textContent = '📋 Copy'; }, 1500); }
    });

    // Screenshot.
    document.getElementById('death-screenshot')?.addEventListener('click', () => {
      canvas.style.filter = '';
      world.renderer.render(world.scene, world.camera);
      const dataUrl = canvas.toDataURL('image/png');
      canvas.style.filter = 'grayscale(80%) brightness(0.6)';
      const a = document.createElement('a');
      a.href = dataUrl; a.download = 'grand-quack-auto-quacked.png'; a.click();
    });

    // Play again — only way to continue.
    document.getElementById('death-retry')?.addEventListener('click', () => {
      window.location.reload();
    });

    // View stats — pulls up the highlights reel for the inevitable
    // "wait, I have to share this" moment.
    document.getElementById('death-stats')?.addEventListener('click', () => {
      canvas.style.filter = '';
      forceShowStatsScreen(world);
    });
  }, 2000);
}

export function isDeathActive(): boolean {
  return active;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
